import {Component, ElementRef, HostBinding, Inject, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {NotificationsService} from 'angular2-notifications';
import {TranslateService} from '@ngx-translate/core';
import {AuthorizationService} from '../../authorization/authorization.service';
import {catchError} from 'rxjs/operators';
import {ModeratorToken} from './moderator-token';
import {timestampToTime} from '../../utility/timestamp-functions';

@Component({
  selector: 'app-moderator-password-popover',
  templateUrl: './moderator-password-popover.component.html',
  styleUrls: ['./moderator-password-popover.component.scss']
})
export class ModeratorPasswordPopoverComponent {
  @ViewChild('moderator_password') inputModeratorPassword: ElementRef;
  @HostBinding('class.header-menu-item') moderatorPasswordContainer = true;
  preloader = false;
  tokenTime = null;
  moderatorFormVisibility = false;
  setIntervalId = null;
  formGroup: FormGroup = new FormGroup({
    moderator_password: new FormControl('', [Validators.required]),
  });

  constructor(
    private http: HttpClient,
    private notif: NotificationsService,
    private translates: TranslateService,
    public authorizationService: AuthorizationService,
    @Inject('ENVIRONMENT_API_BASEPOINT') protected environmentApiBasepoint: string,
  ) {
  }

  toggleModeratorForm() {
    this.moderatorFormVisibility = !this.moderatorFormVisibility;
    this.focusInput();
    this.formGroup.reset();
  }

  focusInput() {
    if (this.moderatorPasswordContainer) {
      setTimeout(() => {
        if (this.inputModeratorPassword && this.inputModeratorPassword.nativeElement) {
          this.inputModeratorPassword.nativeElement.focus();
        }
      }, 0);
    }
  }

  getExpTokenTime(token: string) {
    if (!token) {
      return 0;
    }
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    const expire = payload.exp;
    return expire - now;
  }

  updateTime() {
    if (this.authorizationService.moderatorToken) {
      this.setIntervalId = setInterval(() => {
        const expTime = this.getExpTokenTime(this.authorizationService.moderatorToken);
        this.tokenTime = expTime > 0 ? timestampToTime(expTime) : null;
      }, 1000);
    }
  }

  getTokenTimeStatus() {
    return this.getExpTokenTime(this.authorizationService.moderatorToken) > 0 ?
      this.getExpTokenTime(this.authorizationService.moderatorToken) > 1800 ? 'success' : 'warning'
      : 'default';
  }

  getTokenStatusText() {
    switch (this.getTokenTimeStatus()) {
      case 'success':
        return 'Access to checking extended registration documents is allowed.' +
          ' After the time has expired, the password must be re-entered to reset the timer and continue testing.';
      case 'warning':
        return 'The time allocated for verification is coming to an end.' +
          ' To reset the timer and continue checking documents, enter the password again.';
      case 'default':
        return 'To gain access to the extended registration document check, you must enter the moderator\'s password.' +
          ' Access will be available for 9 hours from the moment of entering the password. After the time expires,' +
          ' you must re-enter the password to continue the verification.';
    }
  }

  openedWindow() {
    this.updateTime();
  }

  closeWindow() {
    this.preloader = false;
    this.moderatorFormVisibility = false;
    this.tokenTime = null;
    clearInterval(this.setIntervalId);
    this.formGroup.reset();
  }

  onSubmitForm() {
    if (this.formGroup.valid) {
      this.preloader = true;
      this.notif.info(this.translates.instant('Loading'), this.translates.instant('please wait'), {id: 'info', timeOut: 0});
      this.http.post(
        this.environmentApiBasepoint + 'moderator-token',
        {moderator_password: this.formGroup.value.moderator_password},
        {observe: 'body'}
      ).pipe(
        catchError((response) => {
            this.preloader = false;
            this.notif.remove('info');
            if (response.error.error_description) {
              this.notif.error(response.error.error_description);
            }
            throw response.error;
          }
        )
      ).subscribe((result: ModeratorToken) => {
        if (result.access_token && result.expires_in) {
          this.notif.remove('info');
          this.notif.success(this.translates.instant('Success'));
          this.authorizationService.storeModeratorToken(result.access_token);
          this.updateTime();
        }
        this.preloader = false;
        this.moderatorFormVisibility = false;
        this.formGroup.reset();
      });
    } else {
      this.notif.alert(this.translates.instant('Form validation error'));
    }
  }

  moderatorTokenDataIsVisible() {
    return this.authorizationService.moderatorToken != null && !this.moderatorFormVisibility;
  }
}
